import React, { useMemo } from 'react'
import { Autocomplete, TextField } from '@mui/material'
import { ReportGroup } from '../../../generated'

interface CombinedOption {
  group: string
  groupLabel: string
  selection: string
  label: string
}

interface ReportSelectionCombinedProps {
  reportTemplates: (ReportGroup | null)[]
  selectedGroup: string
  selectedSelection: string
  onChange: (group: string, selection: string) => void
}

/**
 * A single searchable dropdown that lists every report grouped under its financial
 * group header (bold, non-selectable) with the reports nested beneath. Lets users
 * find a report by name without first knowing which group it belongs to.
 *
 * Note: MUI's `groupBy` only groups *consecutive* options, so the flattened list must
 * stay ordered by group — `reportTemplates` is already grouped, so we keep that order.
 */
export const ReportSelectionCombined: React.FC<ReportSelectionCombinedProps> = ({
  reportTemplates,
  selectedGroup,
  selectedSelection,
  onChange
}) => {
  const options = useMemo<CombinedOption[]>(() => {
    const flattened: CombinedOption[] = []
    reportTemplates?.forEach(group => {
      if (!group) return
      group.selections?.forEach(selection => {
        flattened.push({
          group: group.group,
          groupLabel: group.label || group.group,
          selection: selection.selection,
          label: selection.label || selection.selection
        })
      })
    })
    return flattened
  }, [reportTemplates])

  const value = useMemo(
    () => options.find(option => option.group === selectedGroup && option.selection === selectedSelection) || null,
    [options, selectedGroup, selectedSelection]
  )

  return (
    <Autocomplete
      fullWidth
      options={options}
      groupBy={option => option.groupLabel}
      getOptionLabel={option => option.label || ''}
      isOptionEqualToValue={(option, val) => option.group === val.group && option.selection === val.selection}
      value={value}
      onChange={(_, newValue) => onChange(newValue?.group || '', newValue?.selection || '')}
      ListboxProps={{
        sx: {
          '& .MuiAutocomplete-groupLabel': {
            fontSize: '0.95rem',
            fontWeight: 700,
            color: 'text.primary'
          },
          '& .MuiAutocomplete-option': {
            pl: 7
          }
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label="Report"
          placeholder="Search reports across all groups"
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E0E0E0'
            }
          }}
        />
      )}
    />
  )
}
